import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { DsrService } from '../../core/services/dsr.service';
import { ServiceCatalogService } from '../../core/services/service-catalog.service';
import { InstitutionService } from '../../core/services/institution.service';
import { AttachmentTypeService } from '../../core/services/attachment-type.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import {
  DataSharingRequestResponse, ServiceCatalogResponse, InstitutionResponse,
  DsrEnvironment, DsrStatus, CreateDsrRequest, ApiResponse,
  CreateDsrAttachmentRequest, DsrAttachmentResponse, ServiceRequiredAttachmentDto,
  ServiceDocumentationAttachmentDto, AttachmentTypeDto
} from '../../core/models/models';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ModalComponent, ModalErrorState } from '../../shared/components/modal/modal.component';
import { extractErrorDetails } from '../../core/interceptors/response.interceptor';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-dsr-list',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, ModalComponent, PaginationComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header Title & Action -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight">Data Sharing Requests (DSR)</h1>
          <p class="text-xs text-slate-500">Manage consent authorizations, workflow approvals, and Zan-x-Change data exchange channels.</p>
        </div>

        <button
          (click)="openCreateModal()"
          class="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all active:scale-95 cursor-pointer"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Initiate New DSR Request
        </button>
      </div>

      <!-- Filters & Dynamic Tabs -->
      <div class="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
        <!-- Status & Functional Tabs -->
        <div class="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0">
          <!-- 1. All Requested DSRs -->
          <button
            (click)="switchTab('all')"
            [ngClass]="activeTab() === 'all' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-600 hover:text-slate-900 border-slate-200 bg-slate-50/50 hover:bg-slate-100'"
            class="px-4 py-2 rounded-xl text-xs whitespace-nowrap border transition-all flex items-center gap-2 cursor-pointer"
          >
            <span>All Requested DSRs</span>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200/80 text-slate-700">
              {{ allCount() }}
            </span>
          </button>

          <!-- 2. Incoming DSRs (For Approval & Access Giving) -->
          <button
            (click)="switchTab('incoming')"
            [ngClass]="activeTab() === 'incoming' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-600 hover:text-slate-900 border-slate-200 bg-slate-50/50 hover:bg-slate-100'"
            class="px-4 py-2 rounded-xl text-xs whitespace-nowrap border transition-all flex items-center gap-2 cursor-pointer"
          >
            <div class="flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span>Incoming DSRs (Approval & Access Giving)</span>
            </div>
            <span
              class="px-2 py-0.5 rounded-full text-[10px] font-bold"
              [ngClass]="incomingPendingCount() > 0 ? 'bg-amber-100 text-amber-800 border border-amber-300 font-extrabold' : 'bg-slate-200/80 text-slate-700'"
            >
              {{ incomingCount() }}
              @if (incomingPendingCount() > 0) {
                <span class="text-amber-700">({{ incomingPendingCount() }} Pending)</span>
              }
            </span>
          </button>

          <!-- 3. Outgoing Institutional-Based -->
          <button
            (click)="switchTab('outgoing')"
            [ngClass]="activeTab() === 'outgoing' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-600 hover:text-slate-900 border-slate-200 bg-slate-50/50 hover:bg-slate-100'"
            class="px-4 py-2 rounded-xl text-xs whitespace-nowrap border transition-all flex items-center gap-2 cursor-pointer"
          >
            <div class="flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <span>Outgoing Requests (Institutional)</span>
            </div>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200/80 text-slate-700">
              {{ outgoingCount() }}
            </span>
          </button>
        </div>

        <!-- Filter Controls -->
        <div class="flex items-center gap-3 w-full lg:w-auto">
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (ngModelChange)="currentPage.set(1)"
            placeholder="Search by system, service code or institution..."
            class="w-full lg:w-64 px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
          />

          <select
            [(ngModel)]="statusFilter"
            (ngModelChange)="currentPage.set(1)"
            class="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-xs focus:border-emerald-500 focus:outline-none cursor-pointer shrink-0"
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">PENDING</option>
            <option value="FORWARDED">FORWARDED</option>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
            <option value="ON_HOLD">ON_HOLD</option>
            <option value="RETURNED">RETURNED</option>
          </select>
        </div>
      </div>

      <!-- Tab Informational Banner -->
      @if (activeTab() === 'incoming') {
        <div class="p-3.5 rounded-2xl bg-amber-50/80 border border-amber-200/80 flex items-center justify-between gap-3 text-xs text-amber-900">
          <div class="flex items-center gap-2.5">
            <span class="p-1.5 rounded-xl bg-amber-200/60 text-amber-800">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </span>
            <span>
              <strong>Incoming Approval & Access Giving Portal:</strong> Review requests from consumer institutions seeking access to your service endpoints. You can approve/reject stages, test connectivity, and provision Central Server production live stream access.
            </span>
          </div>
          @if (incomingPendingCount() > 0) {
            <span class="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-amber-200 text-amber-900 border border-amber-300 shrink-0">
              {{ incomingPendingCount() }} Awaiting Your Decision
            </span>
          }
        </div>
      } @else if (activeTab() === 'outgoing') {
        <div class="p-3.5 rounded-2xl bg-blue-50/80 border border-blue-200/80 flex items-center gap-2.5 text-xs text-blue-900">
          <span class="p-1.5 rounded-xl bg-blue-200/60 text-blue-800">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <span>
            <strong>Outgoing Institutional Channels:</strong> Monitor data sharing requests initiated by your institution toward external service providers across the Zanzibar Government data exchange ecosystem.
          </span>
        </div>
      }

      <!-- Data Table -->
      <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-xs">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                @if (activeTab() === 'all') {
                  <th class="p-4">Channel</th>
                }
                <th class="p-4">DSR Ref Code</th>
                <th class="p-4 whitespace-nowrap">Requested Date & Time</th>
                @if (activeTab() === 'incoming') {
                  <th class="p-4">Consumer (Requestor)</th>
                  <th class="p-4">Target Service</th>
                } @else if (activeTab() === 'outgoing') {
                  <th class="p-4">Provider Institution</th>
                  <th class="p-4">Requested Service</th>
                } @else {
                  <th class="p-4">From Institution</th>
                  <th class="p-4">To Provider</th>
                  <th class="p-4">Service & System</th>
                }
                <th class="p-4">Purpose / System</th>
                <th class="p-4">Env</th>
                <th class="p-4">Status & Stage</th>
                <th class="p-4 text-right">Approval & Access Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              @if (isLoading()) {
                <tr>
                  <td [attr.colspan]="activeTab() === 'all' ? 10 : 8" class="p-12 text-center text-slate-500">
                    <div class="inline-flex items-center gap-2 text-emerald-700 font-semibold">
                      <svg class="animate-spin h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                      Loading data sharing requests...
                    </div>
                  </td>
                </tr>
              } @else {
                @for (req of pagedRequests(); track req.uid) {
                  <tr class="hover:bg-slate-50/70 transition-colors">
                    <!-- Direction (on 'all' tab) -->
                    @if (activeTab() === 'all') {
                      <td class="p-4 whitespace-nowrap">
                        @if (isIncomingRequest(req)) {
                          <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                            <svg class="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                            Incoming
                          </span>
                        } @else {
                          <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                            <svg class="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                            </svg>
                            Outgoing
                          </span>
                        }
                      </td>
                    }

                    <!-- Ref Code -->
                    <td class="p-4 font-mono font-bold text-emerald-700 whitespace-nowrap" [title]="req.uid">
                      {{ req.uid.substring(0, 8) }}...
                    </td>

                    <!-- Requested Date & Time -->
                    <td class="p-4 whitespace-nowrap">
                      <div class="flex items-center gap-1.5 font-semibold text-slate-800">
                        <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{{ req.createdAt ? (req.createdAt | date:'mediumDate') : '—' }}</span>
                      </div>
                      <div class="text-[11px] text-slate-500 font-mono pl-5">
                        {{ req.createdAt ? (req.createdAt | date:'shortTime') : '' }}
                      </div>
                    </td>

                    <!-- Dynamic Institution & Service Columns -->
                    @if (activeTab() === 'incoming') {
                      <td class="p-4 font-semibold text-slate-900">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full bg-amber-500"></span>
                          <span>{{ req.fromInstitutionName }}</span>
                        </div>
                      </td>
                      <td class="p-4">
                        <div class="font-bold text-emerald-800">{{ req.serviceName }}</div>
                        <div class="text-[11px] text-slate-500 font-mono">{{ req.serviceCode }}</div>
                      </td>
                    } @else if (activeTab() === 'outgoing') {
                      <td class="p-4 font-semibold text-slate-900">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full bg-blue-500"></span>
                          <span>{{ req.toInstitutionName }}</span>
                        </div>
                      </td>
                      <td class="p-4">
                        <div class="font-bold text-slate-900">{{ req.serviceName }}</div>
                        <div class="text-[11px] text-slate-500 font-mono">{{ req.serviceCode }}</div>
                      </td>
                    } @else {
                      <td class="p-4 font-medium text-slate-800">{{ req.fromInstitutionName }}</td>
                      <td class="p-4 font-medium text-slate-800">{{ req.toInstitutionName }}</td>
                      <td class="p-4">
                        <div class="font-bold text-slate-900">{{ req.serviceName }}</div>
                        <div class="text-[11px] text-slate-500 font-mono">{{ req.serviceCode }}</div>
                      </td>
                    }

                    <!-- Purpose & System -->
                    <td class="p-4 max-w-xs">
                      <div class="font-semibold text-slate-800 truncate" [title]="req.systemName">{{ req.systemName }}</div>
                      <div class="text-[11px] text-slate-500 line-clamp-1" [title]="req.purpose">{{ req.purpose }}</div>
                    </td>

                    <!-- Env -->
                    <td class="p-4 font-mono text-[11px] text-emerald-700 font-bold whitespace-nowrap">
                      {{ req.environment }}
                    </td>

                    <!-- Status & Workflow Stage -->
                    <td class="p-4 whitespace-nowrap">
                      <div class="space-y-1">
                        <app-status-badge [status]="req.status"></app-status-badge>
                        @if (req.currentStageName && req.currentStageName !== 'N/A') {
                          <div class="text-[10px] text-slate-500 font-medium">
                            Stage: <span class="font-semibold text-slate-700">{{ req.currentStageName }}</span>
                          </div>
                        }
                      </div>
                    </td>

                    <!-- Approval & Access Giving Actions -->
                    <td class="p-4 text-right whitespace-nowrap">
                      <div class="inline-flex items-center gap-1.5 justify-end">
                        <!-- If Incoming and Pending: Show quick decision buttons -->
                        @if (isIncomingRequest(req) && (req.status === DsrStatus.PENDING || req.status === DsrStatus.FORWARDED)) {
                          <button
                            (click)="quickApprove(req)"
                            title="Approve Data Sharing Request"
                            class="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-xs flex items-center gap-1 cursor-pointer transition-all active:scale-95"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                          </button>

                          <button
                            (click)="quickReject(req)"
                            title="Reject Data Sharing Request"
                            class="px-2.5 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white font-bold text-xs border border-rose-200 transition-colors cursor-pointer"
                          >
                            Reject
                          </button>
                        }

                        <!-- If Approved or Testing: Show Live Access / Central Server Provisioning (Only to Provider Institutional Admin or SuperAdmin) -->
                        @if (canGrantLiveAccess(req)) {
                          <button
                            (click)="onRequestLiveConnection(req.uid)"
                            title="Authorize Production Live Stream in Central Server"
                            class="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-xs flex items-center gap-1 cursor-pointer transition-all"
                          >
                            <svg class="w-3.5 h-3.5 text-emerald-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                            Grant Live Access
                          </button>

                          <button
                            (click)="onTestConnection(req.uid)"
                            title="Run Zan-x-Change Connection Test"
                            class="px-2 py-1.5 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 font-bold text-xs border border-sky-200 cursor-pointer transition-all"
                          >
                            Test
                          </button>
                        }

                        <!-- Manage / View Details Modal -->
                        <button
                          (click)="openDetails(req)"
                          class="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 font-bold text-slate-700 text-xs transition-colors border border-slate-300 cursor-pointer"
                        >
                          Manage & Details
                        </button>
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td [attr.colspan]="activeTab() === 'all' ? 10 : 8" class="p-12 text-center text-slate-500">
                      <div class="max-w-sm mx-auto space-y-2">
                        <div class="w-10 h-10 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p class="font-semibold text-slate-700">No requests found in this view</p>
                        <p class="text-[11px] text-slate-400">There are currently no data sharing requests matching your selected tab or search filter criteria.</p>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        <app-pagination
          [currentPage]="currentPage()"
          [pageSize]="pageSize()"
          [totalItems]="totalItems()"
          (pageChange)="onPageChange($event)"
          (pageSizeChange)="onPageSizeChange($event)"
        ></app-pagination>
      </div>

      <!-- DSR Request Originating Modal (Direct Service & System Form) -->
      <app-modal
        [isOpen]="showCreateModal()"
        title="DSR Service & System Information"
        maxWidth="max-w-4xl"
        [errorState]="modalError()"
        (close)="showCreateModal.set(false)"
      >
        <div class="space-y-4">
          <!-- 1. Consumer Institution Banner (Logged In Requestor) -->
          <div class="p-3 rounded-2xl bg-gradient-to-r from-emerald-50 via-teal-50/70 to-emerald-50 border border-emerald-200/90 shadow-2xs flex items-center justify-between gap-3">
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center font-black text-xs shadow-xs shrink-0">
                {{ getConsumerInitials() }}
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="text-xs font-bold text-slate-900 leading-tight">{{ authService.institutionName() }}</span>
                  <span class="inline-flex items-center gap-1 px-2 py-0.2 rounded-full text-[9px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase tracking-wide">
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
                    Consumer Node
                  </span>
                </div>
                <div class="text-[10px] text-slate-500 font-medium">Originating entity requesting data exchange access</div>
              </div>
            </div>
          </div>

          <form (ngSubmit)="onCreateDsr()" class="space-y-4">
            <!-- 2. Primary Service & System Form Grid (3 Columns) -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Target Producer Selection -->
              <div>
                <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Target Producer Institution <span class="text-rose-500">*</span>
                </label>
                <select
                  [(ngModel)]="newDsr.toInstitutionUid"
                  (ngModelChange)="onProducerChange($event)"
                  name="toInstitutionUid"
                  required
                  class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-medium focus:border-emerald-500 focus:outline-none transition shadow-2xs cursor-pointer"
                >
                  <option value="" disabled>-- Select Onboarded Producer --</option>
                  @for (producer of onboardedProducers(); track producer.uid) {
                    <option [value]="producer.uid">
                      {{ producer.name }} ({{ producer.memberCode || producer.registrationNumber || 'Node' }})
                    </option>
                  }
                </select>
                <p class="text-[10px] text-slate-400 mt-1">Select external institution hosting requested service.</p>
              </div>

              <!-- Target Service Selection -->
              <div>
                <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Target Service Endpoint <span class="text-rose-500">*</span>
                </label>
                <select
                  [(ngModel)]="newDsr.serviceUid"
                  (ngModelChange)="onServiceChange($event)"
                  name="serviceUid"
                  required
                  [disabled]="!selectedProducerUid() || loadingServices()"
                  class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-medium focus:border-emerald-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer"
                >
                  <option value="" disabled>
                    {{ loadingServices() ? 'Loading active services...' : (!selectedProducerUid() ? '-- Select producer first --' : '-- Choose Service --') }}
                  </option>
                  @for (svc of producerServices(); track svc.uid) {
                    <option [value]="svc.uid">
                      {{ svc.name }} ({{ svc.code }})
                    </option>
                  }
                </select>
                @if (selectedProducerUid() && producerServices().length === 0 && !loadingServices()) {
                  <p class="text-[10px] text-amber-600 font-semibold mt-1">Selected producer has no active services published.</p>
                } @else {
                  <p class="text-[10px] text-slate-400 mt-1">Active APIs in producer's service catalog.</p>
                }
              </div>

              <!-- Consumer Service Name -->
              <div>
                <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Consumer Service Name <span class="text-rose-500">*</span>
                </label>
                @if (consumerServices().length > 0) {
                  <select
                    [(ngModel)]="newDsr.systemName"
                    name="systemName"
                    required
                    class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-medium focus:border-emerald-500 focus:outline-none transition shadow-2xs cursor-pointer"
                  >
                    @for (svc of consumerServices(); track svc.uid) {
                      <option [value]="svc.name">
                        {{ svc.name }} ({{ svc.code }})
                      </option>
                    }
                  </select>
                  <p class="text-[10px] text-slate-400 mt-1">Select from registered services of {{ authService.institutionName() }}.</p>
                } @else {
                  <input
                    type="text"
                    [(ngModel)]="newDsr.systemName"
                    name="systemName"
                    required
                    placeholder="e.g. Core Banking Gateway, HR Portal"
                    class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-medium focus:border-emerald-500 focus:outline-none transition shadow-2xs"
                  />
                  <p class="text-[10px] text-slate-400 mt-1">Gateway for {{ authService.institutionName() }}.</p>
                }
              </div>

              <!-- Row 2, Col 1: Requested Environment (Mandatory TESTING) -->
              <div>
                <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Requested Environment
                </label>
                <div class="flex items-center gap-2.5 px-3.5 py-2 rounded-xl border border-amber-200 bg-amber-50/70 shadow-2xs">
                  <span class="relative flex h-2 w-2 shrink-0">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <div class="min-w-0 flex-1">
                    <span class="font-bold text-xs text-amber-950 block font-mono">TESTING</span>
                    <span class="text-[10px] text-amber-700 block truncate">Sandbox & Verification Pipeline</span>
                  </div>
                  <span class="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
                    Default
                  </span>
                </div>
                <p class="text-[10px] text-slate-400 mt-1">
                  All requests initiate in testing. Production live access is granted after validation.
                </p>
              </div>

              <!-- Row 2, Col 2-3: Purpose & Legal Justification -->
              <div class="md:col-span-2">
                <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Data Use Purpose & Legal Justification <span class="text-rose-500">*</span>
                </label>
                <textarea
                  [(ngModel)]="newDsr.purpose"
                  name="purpose"
                  rows="2"
                  required
                  placeholder="State official justification, legal mandate, and expected data usage under the Zanzibar Data Protection Act..."
                  class="w-full px-3.5 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-medium focus:border-emerald-500 focus:outline-none transition shadow-2xs leading-relaxed resize-y"
                ></textarea>
                <p class="text-[10px] text-slate-400 mt-0.5">Required for compliance governance and immutable security audit trail.</p>
              </div>
            </div>

            <!-- 5. Compliance & Mandatory Documents Upload Section -->
            <div class="pt-4 border-t border-slate-200 space-y-4">
              <!-- Section Header & Progress Bar -->
              <div class="p-4 rounded-2xl bg-gradient-to-r from-emerald-50 via-teal-50/50 to-indigo-50/40 border border-emerald-200/80 shadow-2xs">
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div class="flex items-center gap-3">
                    <span class="p-2.5 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-xs shrink-0">
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </span>
                    <div>
                      <h3 class="text-xs font-black text-slate-900 uppercase tracking-wide flex items-center gap-2">
                        <span>Producer Mandatory Compliance Documents</span>
                        <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                          One-by-One Verification
                        </span>
                      </h3>
                      <p class="text-[11px] text-slate-600 mt-0.5">
                        @if (getSelectedProducerName()) {
                          <span>Required by <strong>{{ getSelectedProducerName() }}</strong> under the Zanzibar Data Protection & Consent Governance framework.</span>
                        } @else {
                          <span>Select a producer institution to view required compliance and legal attachments.</span>
                        }
                      </p>
                    </div>
                  </div>

                  <!-- Mandatory Progress Counter & Bar -->
                  @if (mandatoryServiceAttachments().length > 0) {
                    <div class="flex items-center gap-3 shrink-0 bg-white/80 backdrop-blur-xs px-3.5 py-2 rounded-xl border border-emerald-200/70 shadow-2xs">
                      <div class="text-right">
                        <span class="text-xs font-black" [ngClass]="isAttachmentValid() ? 'text-emerald-700' : 'text-amber-700'">
                          {{ uploadedMandatoryCount() }} / {{ mandatoryServiceAttachments().length }} Uploaded
                        </span>
                        <span class="text-[10px] text-slate-500 font-medium block">
                          {{ isAttachmentValid() ? 'Complete ✓' : (mandatoryServiceAttachments().length - uploadedMandatoryCount()) + ' Remaining' }}
                        </span>
                      </div>
                      <div class="w-14 h-2 rounded-full bg-slate-200 overflow-hidden shrink-0">
                        <div
                          class="h-full transition-all duration-300 rounded-full"
                          [ngClass]="isAttachmentValid() ? 'bg-emerald-600' : 'bg-amber-500'"
                          [style.width.%]="mandatoryProgressPercent()"
                        ></div>
                      </div>
                    </div>
                  }
                </div>

                <!-- Missing Mandatory Banner Alert -->
                @if (!isAttachmentValid() && mandatoryServiceAttachments().length > 0) {
                  <div class="mt-3 p-3 rounded-xl bg-amber-100/90 border border-amber-300 text-amber-900 text-xs flex items-start gap-2.5 shadow-2xs">
                    <svg class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div class="flex-1">
                      <span class="font-extrabold uppercase text-[10px] tracking-wider block text-amber-800">Mandatory Document Requirements Notice</span>
                      <span>Please upload each required document one by one:</span>
                      <strong class="text-amber-950 font-bold ml-1">{{ missingMandatoryNames() }}</strong>.
                      <span class="block text-[10px] text-amber-700 mt-0.5">Submission is blocked until all mandatory files are attached.</span>
                    </div>
                  </div>
                } @else if (isAttachmentValid() && mandatoryServiceAttachments().length > 0) {
                  <div class="mt-3 p-2.5 rounded-xl bg-emerald-100/90 border border-emerald-300 text-emerald-900 text-xs flex items-center gap-2 shadow-2xs">
                    <svg class="w-4 h-4 text-emerald-700 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span class="font-bold">All mandatory compliance documents from producer have been uploaded and validated!</span>
                  </div>
                }
              </div>

              <!-- Mandatory Documents (Rendered One by One) -->
              @if (mandatoryServiceAttachments().length > 0) {
                <div class="space-y-3">
                  <div class="flex items-center justify-between text-[11px] font-extrabold uppercase tracking-wider text-slate-700">
                    <span>Required Documents to Upload (One by One)</span>
                    <span class="text-[10px] text-slate-400 font-medium">Slot assigned per requirement code</span>
                  </div>

                  @for (req of mandatoryServiceAttachments(); track req.code; let idx = $index) {
                    @let uploaded = getUploadedForRequirement(req);
                    <div
                      class="p-4 rounded-2xl border transition-all shadow-2xs"
                      [ngClass]="uploaded
                        ? 'bg-emerald-50/40 border-emerald-300 ring-1 ring-emerald-200'
                        : 'bg-white border-slate-300 hover:border-slate-400 ring-1 ring-slate-100'"
                    >
                      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                        <!-- Left Details: Number, Name, Code, Status, Instructions, Formats -->
                        <div class="space-y-1.5 flex-1 min-w-0">
                          <div class="flex items-center gap-2 flex-wrap">
                            <span
                              class="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                              [ngClass]="uploaded ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white'"
                            >
                              {{ idx + 1 }}
                            </span>
                            <span class="font-black text-xs text-slate-900">{{ req.name }}</span>
                            <span class="px-2 py-0.5 rounded font-mono text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                              {{ req.code }}
                            </span>
                            <span
                              class="px-2 py-0.5 rounded-full text-[10px] font-black tracking-wide border uppercase"
                              [ngClass]="uploaded ? 'bg-emerald-100 text-emerald-900 border-emerald-300' : 'bg-rose-100 text-rose-800 border-rose-300 animate-pulse'"
                            >
                              {{ uploaded ? '✓ UPLOADED & READY' : 'MANDATORY' }}
                            </span>
                          </div>

                          @if (req.description) {
                            <p class="text-xs text-slate-600 leading-relaxed pl-8">{{ req.description }}</p>
                          }

                          <div class="flex items-center gap-3 pl-8 text-[11px] text-slate-500 font-medium flex-wrap">
                            <span class="inline-flex items-center gap-1">
                              <svg class="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              Formats: <strong class="text-slate-700 uppercase font-mono">{{ req.allowedExtensions || 'PDF, DOCX' }}</strong>
                            </span>
                            <span>•</span>
                            <span class="inline-flex items-center gap-1">
                              Max file size: <strong class="text-slate-700">{{ req.maxSizeMb || 10 }} MB</strong>
                            </span>
                          </div>
                        </div>

                        <!-- Right Actions: Single Upload or Uploaded Card with Preview/Replace/Delete -->
                        <div class="shrink-0 flex items-center gap-2 pl-8 lg:pl-0">
                          @if (uploaded) {
                            <div class="flex items-center gap-2 bg-emerald-100/90 p-2 rounded-xl border border-emerald-300 shadow-2xs">
                              <div class="flex items-center gap-2 max-w-[180px] truncate">
                                <svg class="w-5 h-5 text-emerald-700 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div class="truncate">
                                  <div class="text-xs font-bold text-emerald-950 truncate" [title]="uploaded.fileName">{{ uploaded.fileName }}</div>
                                  <div class="text-[10px] text-emerald-800 font-mono">{{ formatFileSize(uploaded.fileSize) }}</div>
                                </div>
                              </div>

                              <!-- Preview Button -->
                              <button
                                type="button"
                                (click)="previewUploadedFile(uploaded)"
                                class="p-1.5 rounded-lg text-emerald-800 hover:text-emerald-950 hover:bg-emerald-200 transition cursor-pointer"
                                title="Preview uploaded file"
                              >
                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>

                              <!-- Replace Button -->
                              <label
                                class="p-1.5 rounded-lg text-indigo-700 hover:text-indigo-950 hover:bg-indigo-100 transition cursor-pointer"
                                title="Replace with a different file"
                              >
                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <input
                                  type="file"
                                  class="hidden"
                                  [accept]="getAcceptAttribute(req.allowedExtensions)"
                                  (change)="onFileSelected($event, req)"
                                />
                              </label>

                              <!-- Remove Button -->
                              <button
                                type="button"
                                (click)="removeUploadedAttachment(uploaded)"
                                class="p-1.5 rounded-lg text-rose-600 hover:text-rose-800 hover:bg-rose-100 transition cursor-pointer"
                                title="Remove document"
                              >
                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          } @else {
                            <!-- Dedicated One-by-One Upload Button for this document slot -->
                            <label
                              class="px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-600/20 transition cursor-pointer inline-flex items-center gap-2 active:scale-95 shrink-0"
                            >
                              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              <span>Upload Document</span>
                              <input
                                type="file"
                                class="hidden"
                                [accept]="getAcceptAttribute(req.allowedExtensions)"
                                (change)="onFileSelected($event, req)"
                              />
                            </label>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Optional Documents (If Producer Specified Any Optional Ones) -->
              @if (optionalServiceAttachments().length > 0) {
                <div class="space-y-2.5 pt-2">
                  <div class="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Optional Compliance Documents (Recommended by Producer)
                  </div>
                  @for (req of optionalServiceAttachments(); track req.code) {
                    @let uploaded = getUploadedForRequirement(req);
                    <div class="p-3 rounded-xl border border-slate-200 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                      <div>
                        <div class="flex items-center gap-2">
                          <span class="font-bold text-slate-900">{{ req.name }}</span>
                          <span class="px-1.5 py-0.2 rounded font-mono text-[9px] bg-slate-200 text-slate-600">{{ req.code }}</span>
                          <span class="px-1.5 py-0.2 rounded text-[9px] font-semibold bg-slate-200 text-slate-700">OPTIONAL</span>
                        </div>
                        <div class="text-[10px] text-slate-400 mt-0.5">Formats: {{ req.allowedExtensions || 'Any' }} • Max {{ req.maxSizeMb || 10 }}MB</div>
                      </div>

                      <div class="shrink-0">
                        @if (uploaded) {
                          <div class="flex items-center gap-2">
                            <span class="text-xs font-semibold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-lg">
                              {{ uploaded.fileName }} ({{ formatFileSize(uploaded.fileSize) }})
                            </span>
                            <button type="button" (click)="removeUploadedAttachment(uploaded)" class="text-rose-500 hover:text-rose-700 text-xs font-bold">Remove</button>
                          </div>
                        } @else {
                          <label class="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 transition cursor-pointer inline-flex items-center gap-1 shadow-2xs">
                            <svg class="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            <span>Upload Optional File</span>
                            <input
                              type="file"
                              class="hidden"
                              [accept]="getAcceptAttribute(req.allowedExtensions)"
                              (change)="onFileSelected($event, req)"
                            />
                          </label>
                        }
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Additional Supporting Attachments -->
              <div class="pt-2">
                <div class="p-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                  <div>
                    <span class="font-bold text-slate-800 block">Additional Supporting Attachments (Optional)</span>
                    <span class="text-[10px] text-slate-400">Attach any extra authorization letters, technical architecture diagrams, or NDA documents.</span>
                  </div>
                  <label class="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 transition cursor-pointer inline-flex items-center gap-1 shrink-0 shadow-2xs">
                    <svg class="w-3.5 h-3.5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Attach Extra File</span>
                    <input type="file" class="hidden" (change)="onFileSelected($event)" />
                  </label>
                </div>

                @if (genericUploadedAttachments().length > 0) {
                  <div class="mt-2 space-y-1.5">
                    @for (att of genericUploadedAttachments(); track att.fileName) {
                      <div class="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs shadow-2xs">
                        <span class="font-medium text-slate-800 truncate">{{ att.fileName }} ({{ formatFileSize(att.fileSize || 0) }})</span>
                        <div class="flex items-center gap-2">
                          <button type="button" (click)="previewUploadedFile(att)" class="text-indigo-600 hover:text-indigo-800 text-[11px] font-bold">Preview</button>
                          <button type="button" (click)="removeUploadedAttachment(att)" class="text-rose-500 hover:text-rose-700 text-[11px] font-bold">Remove</button>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>

            <!-- Duplicate DSR Alert Banner -->
            @if (duplicateDsrWarning(); as dupWarning) {
              <div class="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2.5 shadow-2xs">
                <svg class="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span class="font-medium">{{ dupWarning }}</span>
              </div>
            }

            <!-- 6. Form Action Buttons -->
            <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
              <button
                type="button"
                (click)="showCreateModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-300 hover:bg-slate-200 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                [disabled]="!selectedProducerUid() || !selectedServiceUid() || !newDsr.systemName || !newDsr.purpose || !isAttachmentValid() || !!duplicateDsrWarning()"
                class="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer inline-flex items-center gap-2"
              >
                <span>Submit DSR Request</span>
                @if (!isAttachmentValid()) {
                  <span class="text-[10px] bg-rose-500/80 px-1.5 py-0.5 rounded text-white font-mono">
                    {{ missingMandatoryAttachments().length }} Docs Needed
                  </span>
                }
              </button>
            </div>
          </form>
        </div>
      </app-modal>

      <!-- Details & Workflow / Access Giving Actions Modal -->
      <app-modal
        [isOpen]="showDetailsModal()"
        [title]="'DSR Workflow & Access Authorization'"
        maxWidth="max-w-4xl"
        (close)="showDetailsModal.set(false)"
      >
        @if (selectedDsr(); as dsr) {
          <div class="space-y-6">
            <!-- Header Card with Direction, Code & Requested Date -->
            <div class="p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div class="flex flex-wrap items-center gap-4">
                <div>
                  <span class="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">Reference UID</span>
                  <span class="font-mono text-xs font-bold text-slate-800">{{ dsr.uid }}</span>
                </div>
                <div class="border-l border-slate-200 pl-4">
                  <span class="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block">Requested Date & Time</span>
                  <span class="text-xs font-bold text-slate-800 flex items-center gap-1.5 mt-0.5">
                    <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ dsr.createdAt ? (dsr.createdAt | date:'medium') : '—' }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-2">
                @if (isIncomingRequest(dsr)) {
                  <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300">
                    Incoming Request (Targeting Our Service)
                  </span>
                } @else {
                  <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-900 border border-blue-300">
                    Outgoing Request (Partner Service)
                  </span>
                }
              </div>
            </div>

            <!-- Details Overview Grid (3 or 4 Columns) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs">
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Consumer (Requestor):</span>
                <span class="font-bold text-slate-900 mt-0.5 block">{{ dsr.fromInstitutionName }}</span>
              </div>
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Target Provider:</span>
                <span class="font-bold text-slate-900 mt-0.5 block">{{ dsr.toInstitutionName }}</span>
              </div>
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Target Service Endpoint:</span>
                <span class="font-semibold text-emerald-800 mt-0.5 block">{{ dsr.serviceName }} ({{ dsr.serviceCode }})</span>
              </div>
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Consumer System:</span>
                <span class="font-semibold text-slate-800 mt-0.5 block">{{ dsr.systemName }}</span>
              </div>
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Status & Environment:</span>
                <div class="flex items-center gap-2 mt-1">
                  <app-status-badge [status]="dsr.status"></app-status-badge>
                  <span class="font-mono text-emerald-700 font-bold">{{ dsr.environment }}</span>
                </div>
              </div>
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Workflow Stage:</span>
                <span class="font-bold text-slate-800 mt-1 block">{{ dsr.currentStageName || 'Initial Review' }}</span>
              </div>
              <div>
                <span class="text-slate-500 block text-[11px] font-medium">Requested Date & Time:</span>
                <span class="font-bold text-slate-900 mt-1 block">
                  {{ dsr.createdAt ? (dsr.createdAt | date:'medium') : '—' }}
                </span>
              </div>
              @if (dsr.updatedAt) {
                <div>
                  <span class="text-slate-500 block text-[11px] font-medium">Last Status Update:</span>
                  <span class="font-semibold text-slate-800 mt-1 block">
                    {{ dsr.updatedAt | date:'medium' }}
                  </span>
                </div>
              }
            </div>

            <!-- Declared Purpose -->
            <div>
              <span class="text-xs font-bold text-slate-700 uppercase block mb-1">Declared Legal Purpose</span>
              <p class="text-xs text-slate-700 p-3 rounded-xl bg-white border border-slate-200 leading-relaxed shadow-2xs">
                {{ dsr.purpose }}
              </p>
            </div>

            <!-- Uploaded Compliance Attachments Section -->
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-xs font-bold text-slate-700 uppercase block">Compliance Attachments & Verification Documents</span>
                <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {{ (dsr.attachments?.length || 0) }} Document{{ (dsr.attachments?.length || 0) === 1 ? '' : 's' }} Attached
                </span>
              </div>

              @if (dsr.attachments && dsr.attachments.length > 0) {
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  @for (att of dsr.attachments; track att.uid) {
                    <div class="p-3 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 transition shadow-2xs flex items-center justify-between gap-3">
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5">
                          <svg class="w-4 h-4 text-indigo-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span class="font-bold text-xs text-slate-900 truncate" [title]="att.attachmentName">{{ att.attachmentName }}</span>
                        </div>
                        <div class="text-[10px] text-slate-500 truncate mt-0.5 font-mono">
                          {{ att.fileName }} @if (att.fileSize) { <span>• {{ formatFileSize(att.fileSize) }}</span> }
                        </div>
                      </div>

                      @if (att.fileData) {
                        <div class="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            (click)="previewUploadedFile(att)"
                            class="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-indigo-50 hover:bg-indigo-600 text-indigo-700 hover:text-white border border-indigo-200 transition cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                            title="Preview compliance document"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>Preview</span>
                          </button>
                          <a
                            [href]="att.fileData"
                            [download]="att.fileName"
                            class="p-1.5 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 transition cursor-pointer inline-flex items-center shadow-2xs"
                            title="Download document"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                        </div>
                      }
                    </div>
                  }
                </div>
              } @else {
                <div class="p-3.5 rounded-xl border border-dashed border-slate-200 text-center bg-slate-50/50 text-slate-400 text-xs">
                  No compliance attachments uploaded for this request.
                </div>
              }
            </div>

            <!-- Provider Service API Documentation & Technical Specifications -->
            <div class="space-y-2.5 pt-3 border-t border-slate-200">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-bold text-slate-900 uppercase block">Provider Service Documentation & API Specifications</span>
                    <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-800 border border-teal-200">
                      {{ (dsr.serviceDocumentationAttachments?.length || 0) }} Specification{{ (dsr.serviceDocumentationAttachments?.length || 0) === 1 ? '' : 's' }}
                    </span>
                  </div>
                  <p class="text-[10px] text-slate-500 mt-0.5">
                    Institution-specific technical documentation, OpenAPI specs, and integration guidelines provisioned by {{ dsr.toInstitutionName }}.
                  </p>
                </div>

                @if (canManageDocumentation(dsr)) {
                  <button
                    type="button"
                    (click)="openUploadDocModal()"
                    class="px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 transition shadow-sm inline-flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Upload Specification</span>
                  </button>
                }
              </div>

              @if (dsr.serviceDocumentationAttachments && dsr.serviceDocumentationAttachments.length > 0) {
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  @for (doc of dsr.serviceDocumentationAttachments; track doc.uid) {
                    <div class="p-3 rounded-xl border border-slate-200 bg-white hover:border-teal-300 transition shadow-2xs flex items-center justify-between gap-3">
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5">
                          @if (doc.docType === 'OPEN_API') {
                            <svg class="w-4 h-4 text-teal-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                          } @else {
                            <svg class="w-4 h-4 text-teal-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          }
                          <span class="font-bold text-xs text-slate-900 truncate" [title]="doc.title">{{ doc.title }}</span>
                        </div>
                        <div class="flex items-center gap-1.5 mt-0.5">
                          <span class="px-1.5 py-0.2 rounded text-[9px] font-bold bg-teal-50 text-teal-800 border border-teal-200">
                            {{ formatDocType(doc.docType) }}
                          </span>
                          <span class="text-[10px] text-slate-500 truncate font-mono">
                            {{ doc.fileName }} @if (doc.fileSize) { <span>• {{ formatFileSize(doc.fileSize) }}</span> }
                          </span>
                        </div>
                      </div>

                      <div class="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          (click)="openDocPreview(doc)"
                          class="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-teal-50 hover:bg-teal-600 text-teal-700 hover:text-white border border-teal-200 transition cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                          title="Preview document / OpenAPI specification"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>Preview</span>
                        </button>

                        @if (doc.fileData) {
                          <a
                            [href]="doc.fileData"
                            [download]="doc.fileName"
                            class="px-2 py-1.5 rounded-lg text-xs font-bold bg-slate-100 hover:bg-slate-800 text-slate-700 hover:text-white border border-slate-300 transition cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                            title="Download specification"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                        }

                        @if (canManageDocumentation(dsr)) {
                          <button
                            type="button"
                            (click)="removeDsrDoc(doc)"
                            class="p-1.5 rounded-lg text-rose-500 hover:text-white hover:bg-rose-500 border border-transparent hover:border-rose-500 transition cursor-pointer"
                            title="Remove attached specification"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        }
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div class="p-4 rounded-xl border border-dashed border-slate-200 text-center bg-slate-50/50 space-y-1">
                  @if (canManageDocumentation(dsr)) {
                    <p class="text-xs font-semibold text-slate-600">No specifications uploaded for {{ dsr.fromInstitutionName }} yet.</p>
                    <p class="text-[11px] text-slate-400">Click <strong>"Upload Specification"</strong> above to attach OpenAPI 3.0 specs, WSDL, or integration manuals tailored specifically for this consumer.</p>
                  } @else {
                    <p class="text-xs font-semibold text-slate-600">No technical documentation or API specifications available yet.</p>
                    <p class="text-[11px] text-slate-400">The provider institution ({{ dsr.toInstitutionName }}) will attach tailored integration documentation and endpoints once access is provisioned.</p>
                  }
                </div>
              }
            </div>

            <!-- Officer Approval & Access Giving Actions -->
            <div class="p-5 rounded-2xl bg-emerald-50/70 border border-emerald-200 space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-xs font-bold text-emerald-900 uppercase tracking-wider block">
                  Approval & Access Giving Controls
                </span>
                <span class="text-[11px] text-emerald-700 font-medium">Authorized Institutional Officer Actions</span>
              </div>

              <!-- Officer Remarks Input for Approval / Rejection -->
              @if (isIncomingRequest(dsr) && (dsr.status === DsrStatus.PENDING || dsr.status === DsrStatus.FORWARDED)) {
                <div>
                  <label class="block text-xs font-bold text-slate-700 mb-1">
                    Officer Decision Remarks / Conditions
                  </label>
                  <input
                    type="text"
                    [(ngModel)]="workflowComment"
                    placeholder="Enter approval conditions or rejection reason..."
                    class="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 text-xs focus:border-emerald-500 focus:outline-none shadow-2xs"
                  />
                </div>
              }

              <div class="flex flex-wrap items-center gap-3 pt-1">
                <!-- Approval Actions for Incoming Requests -->
                @if (isIncomingRequest(dsr) && (dsr.status === DsrStatus.PENDING || dsr.status === DsrStatus.FORWARDED)) {
                  <button
                    (click)="onProcessAction(dsr.uid, 'APPROVE')"
                    [disabled]="isProcessingAction()"
                    class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Approve Request Stage
                  </button>

                  <button
                    (click)="onProcessAction(dsr.uid, 'REJECT')"
                    [disabled]="isProcessingAction()"
                    class="px-4 py-2 rounded-xl bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-700 font-bold text-xs border border-rose-200 flex items-center gap-1.5 cursor-pointer disabled:opacity-50 transition-colors"
                  >
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject DSR
                  </button>
                }

                <!-- Access Provisioning Controls (Authorize Production Live / Test - Only for Provider Institutional Admin or SuperAdmin) -->
                @if (canGrantLiveAccess(dsr)) {
                  <button
                    (click)="onRequestLiveConnection(dsr.uid)"
                    [disabled]="isProcessingAction()"
                    class="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <svg class="w-4 h-4 text-emerald-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Authorize Production Live Stream (Access Giving)
                  </button>

                  <button
                    (click)="onTestConnection(dsr.uid)"
                    [disabled]="isProcessingAction()"
                    class="px-3.5 py-2 rounded-xl bg-white hover:bg-slate-100 text-emerald-800 font-bold text-xs flex items-center gap-1.5 border border-emerald-300 shadow-2xs cursor-pointer disabled:opacity-50"
                  >
                    <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Run Zan-x-Change Connection Test
                  </button>
                } @else if (dsr.status === DsrStatus.APPROVED || dsr.status === DsrStatus.TESTING) {
                  <div class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                    <svg class="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Request Approved &bull; Awaiting Live Access Provisioning by {{ dsr.toInstitutionName || 'Provider Institution' }} Admin</span>
                  </div>
                } @else if (dsr.status === 'LIVE') {
                  <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-100 text-emerald-900 border border-emerald-300 text-xs font-bold">
                    <span class="w-2 h-2 rounded-full bg-emerald-600 animate-pulse"></span>
                    Production Access Active in Central Server
                  </div>
                }
              </div>
            </div>

            <!-- Workflow Progress Timeline -->
            @if (dsr.progressHistory && dsr.progressHistory.length > 0) {
              <div>
                <span class="text-xs font-bold text-slate-700 uppercase block mb-3">Workflow Progress History</span>
                <div class="space-y-3 pl-2 border-l-2 border-slate-200">
                  @for (prog of dsr.progressHistory; track $index) {
                    <div class="relative pl-4">
                      <span class="absolute -left-[11px] top-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"></span>
                      <div class="text-xs font-bold text-slate-900">
                        {{ prog.stageName || 'Workflow Step' }} - <span class="text-emerald-700">{{ prog.status }}</span>
                      </div>
                      <div class="text-[11px] text-slate-500">
                        {{ prog.comment || 'Action recorded' }} • By <span class="font-medium text-slate-700">{{ prog.createdByName || prog.performedBy || 'Officer' }}</span>
                        @if (prog.createdAt || prog.timestamp) {
                          <span class="text-slate-400 font-mono text-[10px]"> • {{ (prog.createdAt || prog.timestamp) | date:'medium' }}</span>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        }
      </app-modal>

      <!-- Interactive Document & Specification Preview Modal -->
      <app-modal
        [isOpen]="showDocPreviewModal()"
        [title]="previewDoc()?.title || 'Specification Preview'"
        maxWidth="max-w-4xl"
        (close)="showDocPreviewModal.set(false)"
      >
        @if (previewDoc(); as doc) {
          <div class="space-y-4">
            <!-- Header banner with info & quick actions -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-200">
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-bold text-xs text-slate-900 truncate">{{ doc.title }}</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-50 text-teal-800 border border-teal-200">
                    {{ formatDocType(doc.docType) }}
                  </span>
                </div>
                <div class="text-[10px] text-slate-500 font-mono mt-0.5">
                  {{ doc.fileName }} &bull; {{ formatFileSize(doc.fileSize) }}
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                @if (previewTextContent()) {
                  <button
                    type="button"
                    (click)="copyPreviewContent()"
                    class="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                  >
                    <svg class="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Specification</span>
                  </button>
                }

                @if (doc.fileData) {
                  <a
                    [href]="doc.fileData"
                    [download]="doc.fileName"
                    class="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download</span>
                  </a>
                }
              </div>
            </div>

            <!-- Preview Body -->
            @if (previewTextContent()) {
              <div class="rounded-xl border border-slate-800 bg-slate-950 p-4 max-h-[460px] overflow-auto shadow-inner text-slate-200">
                <pre class="font-mono text-xs leading-relaxed whitespace-pre-wrap selection:bg-teal-700 selection:text-white">{{ previewTextContent() }}</pre>
              </div>
            } @else if (isPdf(doc) && pdfUrl()) {
              <div class="rounded-xl border border-slate-200 overflow-hidden bg-slate-100 shadow-inner h-[500px]">
                <iframe [src]="pdfUrl()" class="w-full h-full border-0" title="PDF Document Viewer"></iframe>
              </div>
            } @else if (isImage(doc) && doc.fileData) {
              <div class="rounded-xl border border-slate-200 overflow-hidden bg-slate-900/5 p-4 flex justify-center max-h-[500px] overflow-auto">
                <img [src]="doc.fileData" [alt]="doc.fileName" class="max-h-[460px] object-contain rounded-lg shadow-md" />
              </div>
            } @else {
              <div class="p-8 rounded-2xl border border-dashed border-slate-200 text-center bg-slate-50 space-y-3">
                <div class="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold text-slate-800 text-sm">Binary or Document File</h4>
                  <p class="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    Direct in-browser text rendering is not supported for this file format (<strong>{{ doc.fileName }}</strong>). Please download the file to inspect in your local viewer or OpenAPI editor.
                  </p>
                </div>
                @if (doc.fileData) {
                  <div>
                    <a
                      [href]="doc.fileData"
                      [download]="doc.fileName"
                      class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 transition shadow-sm cursor-pointer"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span>Download {{ doc.fileName }} ({{ formatFileSize(doc.fileSize) }})</span>
                    </a>
                  </div>
                }
              </div>
            }

            <div class="flex justify-end pt-3 border-t border-slate-200">
              <button
                type="button"
                (click)="showDocPreviewModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        }
      </app-modal>

      <!-- Uploaded Document Preview Modal -->
      <app-modal
        [isOpen]="showUploadPreviewModal()"
        [title]="selectedUploadedPreview()?.attachmentName || 'Uploaded Compliance Document'"
        maxWidth="max-w-4xl"
        (close)="showUploadPreviewModal.set(false)"
      >
        @if (selectedUploadedPreview(); as att) {
          <div class="space-y-4">
            <div class="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between">
              <div>
                <div class="font-bold text-xs text-emerald-950">{{ att.fileName }}</div>
                <div class="text-[11px] text-emerald-700 font-mono mt-0.5">
                  {{ formatFileSize(att.fileSize) }} • {{ att.contentType || 'application/octet-stream' }}
                </div>
              </div>
              @if (att.fileData) {
                <a
                  [href]="att.fileData"
                  [download]="att.fileName"
                  class="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-600 transition shadow-2xs inline-flex items-center gap-1.5"
                >
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span>Download Document</span>
                </a>
              }
            </div>

            @if (isUploadedPdf(att) && uploadedPdfUrl()) {
              <div class="rounded-xl border border-slate-200 overflow-hidden bg-slate-100 shadow-inner h-[500px]">
                <iframe [src]="uploadedPdfUrl()" class="w-full h-full border-0" title="Uploaded PDF Preview"></iframe>
              </div>
            } @else if (isUploadedImage(att) && att.fileData) {
              <div class="rounded-xl border border-slate-200 overflow-hidden bg-slate-900/5 p-4 flex justify-center max-h-[500px] overflow-auto">
                <img [src]="att.fileData" [alt]="att.fileName" class="max-h-[460px] object-contain rounded-lg shadow-md" />
              </div>
            } @else {
              <div class="p-8 rounded-2xl border border-dashed border-slate-200 text-center bg-slate-50 space-y-3">
                <div class="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold text-slate-800 text-sm">Document Encoded & Verified</h4>
                  <p class="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    This file (<strong>{{ att.fileName }}</strong>) has been processed for secure transmission under the Zanzibar Data Protection framework. You can download it to inspect or submit.
                  </p>
                </div>
              </div>
            }

            <div class="flex justify-end pt-3 border-t border-slate-200">
              <button
                type="button"
                (click)="showUploadPreviewModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        }
      </app-modal>

      <!-- Upload DSR Specification Modal -->
      <app-modal
        [isOpen]="showUploadDocModal()"
        title="Upload Service Documentation & API Specification"
        maxWidth="max-w-2xl"
        (close)="showUploadDocModal.set(false)"
      >
        <div class="space-y-4">
          <div class="p-3 rounded-xl bg-teal-50 border border-teal-200 text-teal-900 text-xs leading-relaxed">
            Attach tailored OpenAPI specifications, endpoint URLs, integration guides, or payload schemas specifically for <strong>{{ selectedDsr()?.fromInstitutionName }}</strong>.
          </div>

          <div class="space-y-3">
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Specification Title / Label *</label>
              <input
                type="text"
                [(ngModel)]="newDsrDoc().title"
                name="dsrDocTitle"
                placeholder="e.g. REST API Specification v1.0 (OpenAPI 3.0)"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-teal-500 focus:outline-none shadow-2xs"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Document Category</label>
              <select
                [(ngModel)]="newDsrDoc().docType"
                name="dsrDocType"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-teal-500 focus:outline-none shadow-2xs cursor-pointer bg-white"
              >
                <option value="OPEN_API">OpenAPI 3.0 / Swagger Spec</option>
                <option value="GUIDELINE">Integration Guideline / Manual</option>
                <option value="TECH_SPEC">Technical Specification / WSDL</option>
                <option value="SCHEMA">Payload Schema / Sample JSON</option>
                <option value="OTHER">General Documentation / PDF</option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Upload File *</label>
              <div class="p-4 rounded-xl border-2 border-dashed border-slate-300 hover:border-teal-500 transition text-center bg-slate-50/50 cursor-pointer relative">
                <input
                  type="file"
                  accept=".json,.yaml,.yml,.pdf,.docx,.txt,.md,.xml"
                  (change)="onDsrDocFileSelected($event)"
                  class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                @if (newDsrDoc().fileName) {
                  <div class="space-y-1">
                    <div class="flex items-center justify-center gap-2 text-teal-700 font-bold text-xs">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{{ newDsrDoc().fileName }}</span>
                    </div>
                    <p class="text-[10px] text-slate-500 font-mono">{{ formatFileSize(newDsrDoc().fileSize) }} • Click or drag to replace file</p>
                  </div>
                } @else {
                  <div class="space-y-1">
                    <svg class="w-8 h-8 text-slate-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="text-xs font-semibold text-slate-700">Click to browse or drop file here</p>
                    <p class="text-[10px] text-slate-400">Supported: JSON, YAML, PDF, DOCX, TXT, Markdown, XML (up to 25MB)</p>
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-2.5 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showUploadDocModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="submitDsrDoc()"
              [disabled]="isUploadingDoc() || !newDsrDoc().fileData || !newDsrDoc().title.trim()"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm inline-flex items-center gap-2 cursor-pointer"
            >
              @if (isUploadingDoc()) {
                <svg class="animate-spin w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Attaching...</span>
              } @else {
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Save & Attach Specification</span>
              }
            </button>
          </div>
        </div>
      </app-modal>
    </div>
  `
})
export class DsrListComponent implements OnInit {
  dsrService = inject(DsrService);
  catalogService = inject(ServiceCatalogService);
  institutionService = inject(InstitutionService);
  attachmentTypeService = inject(AttachmentTypeService);
  toastService = inject(ToastService);
  authService = inject(AuthService);
  sanitizer = inject(DomSanitizer);
  route = inject(ActivatedRoute);

  DsrEnvironment = DsrEnvironment;
  DsrStatus = DsrStatus;

  activeTab = signal<'all' | 'incoming' | 'outgoing'>('all');
  statusFilter = 'ALL';
  searchQuery = '';

  allCount = signal<number>(0);
  incomingCount = signal<number>(0);
  incomingPendingCount = signal<number>(0);
  outgoingCount = signal<number>(0);

  isLoading = signal<boolean>(false);
  isProcessingAction = signal<boolean>(false);
  workflowComment = '';

  requests = signal<DataSharingRequestResponse[]>([]);
  allActiveServices = signal<ServiceCatalogResponse[]>([]);
  onboardedInstitutions = signal<InstitutionResponse[]>([]);
  producerServices = signal<ServiceCatalogResponse[]>([]);
  consumerServices = signal<ServiceCatalogResponse[]>([]);
  loadingServices = signal<boolean>(false);

  selectedProducerUid = signal<string>('');
  selectedServiceUid = signal<string>('');
  producerAttachmentTypes = signal<AttachmentTypeDto[]>([]);
  selectedServiceDetail = signal<ServiceCatalogResponse | null>(null);

  showCreateModal = signal<boolean>(false);
  showDetailsModal = signal<boolean>(false);
  selectedDsr = signal<DataSharingRequestResponse | null>(null);
  modalError = signal<ModalErrorState | null>(null);

  // Documentation preview modal state
  showDocPreviewModal = signal<boolean>(false);
  previewDoc = signal<ServiceDocumentationAttachmentDto | null>(null);
  previewTextContent = signal<string | null>(null);
  pdfUrl = computed<SafeResourceUrl | null>(() => {
    const doc = this.previewDoc();
    if (doc && this.isPdf(doc) && doc.fileData) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(doc.fileData);
    }
    return null;
  });

  // Upload preview modal state
  selectedUploadedPreview = signal<CreateDsrAttachmentRequest | DsrAttachmentResponse | null>(null);
  showUploadPreviewModal = signal<boolean>(false);
  uploadedPdfUrl = computed<SafeResourceUrl | null>(() => {
    const att = this.selectedUploadedPreview();
    if (att && this.isUploadedPdf(att) && att.fileData) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(att.fileData);
    }
    return null;
  });

  // DSR Documentation / API Specification Upload state
  showUploadDocModal = signal<boolean>(false);
  isUploadingDoc = signal<boolean>(false);
  newDsrDoc = signal<{
    title: string;
    docType: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    fileData: string;
  }>({
    title: '',
    docType: 'OPEN_API',
    fileName: '',
    contentType: '',
    fileSize: 0,
    fileData: ''
  });

  newDsr: CreateDsrRequest = {
    toInstitutionUid: '',
    serviceUid: '',
    systemName: '',
    environment: DsrEnvironment.TESTING,
    purpose: ''
  };

  uploadedAttachments = signal<CreateDsrAttachmentRequest[]>([]);

  selectedServiceAttachments = computed<ServiceRequiredAttachmentDto[]>(() => {
    const sUid = this.selectedServiceUid();
    const pUid = this.selectedProducerUid();
    if (!sUid && !pUid) return [];

    // 1. Check detailed service catalog item
    const detail = this.selectedServiceDetail();
    if (detail && detail.requiredAttachments && detail.requiredAttachments.length > 0) {
      return detail.requiredAttachments;
    }

    // 2. Check cached producer services
    const srv = this.producerServices().find(s => s.uid === sUid)
      || this.allActiveServices().find(s => s.uid === sUid);
    if (srv && srv.requiredAttachments && srv.requiredAttachments.length > 0) {
      return srv.requiredAttachments;
    }

    // 3. Fall back to compliance attachment types defined for the producer institution or system
    const types = this.producerAttachmentTypes();
    if (types && types.length > 0) {
      return types.map(t => ({
        code: t.code,
        name: t.name,
        description: t.description,
        isRequired: t.isRequired ?? true,
        allowedExtensions: t.allowedExtensions || 'pdf,docx',
        maxSizeMb: t.maxSizeMb || 10
      }));
    }

    // 4. Default Zanzibar Data Sharing Agreement requirement if no custom list defined
    return [
      {
        code: 'DSA',
        name: 'Data Sharing Agreement (DSA)',
        description: 'Bilateral compliance contract binding data privacy, purpose limitation, and cybersecurity protocols between data producer and consumer.',
        isRequired: true,
        allowedExtensions: 'pdf,docx',
        maxSizeMb: 10
      }
    ];
  });

  mandatoryServiceAttachments = computed<ServiceRequiredAttachmentDto[]>(() => {
    return this.selectedServiceAttachments().filter(a => a.isRequired);
  });

  duplicateDsrWarning = computed<string | null>(() => {
    const pUid = this.selectedProducerUid();
    const sUid = this.selectedServiceUid();
    const sysName = this.newDsr.systemName?.trim().toLowerCase();
    if (!pUid || !sUid || !sysName) return null;

    const existing = this.requests().find(r => {
      const matchProducer = r.toInstitutionUid === pUid;
      const matchService = r.serviceUid === sUid;
      const matchSys = r.systemName?.trim().toLowerCase() === sysName;
      const isNotRejected = r.status !== DsrStatus.REJECTED;
      return matchProducer && matchService && matchSys && isNotRejected;
    });

    if (existing) {
      return `Duplicate Request: An active Data Sharing Request already exists with ${existing.toInstitutionName || 'this provider'} for service '${existing.serviceName || 'requested service'}' under system '${existing.systemName}' (Current Status: ${existing.status}).`;
    }
    return null;
  });

  optionalServiceAttachments = computed<ServiceRequiredAttachmentDto[]>(() => {
    return this.selectedServiceAttachments().filter(a => !a.isRequired);
  });

  genericUploadedAttachments = computed(() => {
    const reqCodes = this.selectedServiceAttachments().map(r => r.code.toLowerCase());
    return this.uploadedAttachments().filter(u => !u.attachmentCode || !reqCodes.includes(u.attachmentCode.toLowerCase()));
  });

  missingMandatoryAttachments = computed<ServiceRequiredAttachmentDto[]>(() => {
    const required = this.mandatoryServiceAttachments();
    const uploaded = this.uploadedAttachments();
    return required.filter(req => !uploaded.some(u =>
      (u.attachmentCode && req.code && u.attachmentCode.toLowerCase() === req.code.toLowerCase()) ||
      (u.attachmentName && req.name && u.attachmentName.toLowerCase() === req.name.toLowerCase())
    ));
  });

  uploadedMandatoryCount = computed<number>(() => {
    const total = this.mandatoryServiceAttachments().length;
    const missing = this.missingMandatoryAttachments().length;
    return total - missing;
  });

  mandatoryProgressPercent = computed<number>(() => {
    const total = this.mandatoryServiceAttachments().length;
    if (total === 0) return 100;
    return Math.round((this.uploadedMandatoryCount() / total) * 100);
  });

  missingMandatoryNames = computed<string>(() => {
    return this.missingMandatoryAttachments().map(m => m.name).join(', ');
  });

  isAttachmentValid = computed<boolean>(() => {
    return this.missingMandatoryAttachments().length === 0;
  });

  // Compute onboarded producer institutions excluding the current logged-in institution
  onboardedProducers = computed(() => {
    const myInstUid = this.authService.institutionUid();
    const myInstName = (this.authService.institutionName() || '').trim().toLowerCase();

    return this.onboardedInstitutions().filter(inst => {
      if (myInstUid && inst.uid && String(inst.uid).toLowerCase() === String(myInstUid).toLowerCase()) {
        return false;
      }
      if (myInstName && inst.name && inst.name.trim().toLowerCase() === myInstName) {
        return false;
      }
      return true;
    });
  });

  filteredRequests = computed(() => {
    let list = this.requests();
    if (this.statusFilter !== 'ALL') {
      list = list.filter(r => r.status === this.statusFilter);
    }
    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(r =>
        (r.systemName && r.systemName.toLowerCase().includes(q)) ||
        (r.serviceCode && r.serviceCode.toLowerCase().includes(q)) ||
        (r.serviceName && r.serviceName.toLowerCase().includes(q)) ||
        (r.fromInstitutionName && r.fromInstitutionName.toLowerCase().includes(q)) ||
        (r.toInstitutionName && r.toInstitutionName.toLowerCase().includes(q))
      );
    }
    return list;
  });

  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  totalItems = computed(() => this.filteredRequests().length);

  pagedRequests = computed(() => {
    const list = this.filteredRequests();
    const start = (this.currentPage() - 1) * this.pageSize();
    return list.slice(start, start + this.pageSize());
  });

  onPageChange(page: number) {
    this.currentPage.set(page);
  }

  onPageSizeChange(newSize: number) {
    this.pageSize.set(newSize);
    this.currentPage.set(1);
  }

  ngOnInit() {
    this.loadRequests();
    this.loadInstitutionsAndServices();
    this.refreshCounts();

    this.route.queryParams.subscribe(params => {
      if (params['requestDsr'] === 'true' || params['producerUid'] || params['serviceUid']) {
        this.openCreateModal(params['producerUid'], params['serviceUid']);
      }
    });
  }

  isIncomingRequest(req: DataSharingRequestResponse): boolean {
    const myInstUid = this.authService.institutionUid();
    const myInstName = (this.authService.institutionName() || '').trim().toLowerCase();

    if (myInstUid && req.toInstitutionUid && String(req.toInstitutionUid).toLowerCase() === String(myInstUid).toLowerCase()) {
      return true;
    }
    if (myInstName && req.toInstitutionName && req.toInstitutionName.trim().toLowerCase() === myInstName) {
      return true;
    }
    return false;
  }

  canGrantLiveAccess(req: DataSharingRequestResponse | null | undefined): boolean {
    if (!req) return false;
    if (req.status !== DsrStatus.APPROVED && req.status !== DsrStatus.TESTING) {
      return false;
    }

    // SuperAdmin / Internal has system-wide administrative authorization
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }

    // Must be an Institutional Admin
    const isInstAdmin = this.authService.hasRole('InstitutionAdmin') || this.authService.isInstitutionAdmin();
    if (!isInstAdmin) {
      return false;
    }

    // Must belong specifically to the provider institution (toInstitution) where request was approved
    return this.isIncomingRequest(req);
  }

  switchTab(tab: 'all' | 'incoming' | 'outgoing') {
    this.currentPage.set(1);
    this.activeTab.set(tab);
    this.loadRequests();
  }

  loadRequests() {
    this.isLoading.set(true);
    let req$: Observable<ApiResponse<DataSharingRequestResponse[]>>;

    if (this.activeTab() === 'all') {
      req$ = this.dsrService.getAllRequests();
    } else if (this.activeTab() === 'incoming') {
      req$ = this.dsrService.getIncomingRequests();
    } else {
      req$ = this.dsrService.getOutgoingRequests();
    }

    req$.subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.requests.set(res.data);
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.toastService.error('Failed to load data sharing requests');
      }
    });

    this.refreshCounts();
  }

  refreshCounts() {
    this.dsrService.getAllRequests().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const all = res.data;
          this.allCount.set(all.length);

          const incoming = all.filter(r => this.isIncomingRequest(r));
          this.incomingCount.set(incoming.length);
          this.incomingPendingCount.set(incoming.filter(r => r.status === DsrStatus.PENDING || r.status === DsrStatus.FORWARDED).length);

          const outgoing = all.filter(r => !this.isIncomingRequest(r));
          this.outgoingCount.set(outgoing.length);
        }
      }
    });
  }

  loadInstitutionsAndServices() {
    this.institutionService.getAllInstitutions().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.onboardedInstitutions.set(res.data);
        }
      }
    });

    this.catalogService.getActiveServices().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.allActiveServices.set(res.data);
          this.updateConsumerServices(res.data);
        }
      }
    });

    const myInstUid = this.authService.institutionUid();
    if (myInstUid) {
      this.catalogService.getServicesByInstitution(myInstUid).subscribe({
        next: (res) => {
          if (res.success && res.data && res.data.length > 0) {
            this.consumerServices.set(res.data);
            if (!this.newDsr.systemName) {
              this.newDsr.systemName = res.data[0].name;
            }
          }
        }
      });
    }
  }

  updateConsumerServices(services: ServiceCatalogResponse[]) {
    const myInstUid = this.authService.institutionUid();
    const myInstName = (this.authService.institutionName() || '').trim().toLowerCase();

    const matching = services.filter(s => {
      if (myInstUid && s.institutionUid && String(s.institutionUid).toLowerCase() === String(myInstUid).toLowerCase()) {
        return true;
      }
      if (myInstName && s.institutionName && s.institutionName.trim().toLowerCase() === myInstName) {
        return true;
      }
      return false;
    });

    if (matching.length > 0) {
      this.consumerServices.set(matching);
      if (!this.newDsr.systemName) {
        this.newDsr.systemName = matching[0].name;
      }
    }
  }

  getConsumerInitials(): string {
    const name = this.authService.institutionName();
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  onProducerChange(producerUid: string) {
    this.selectedProducerUid.set(producerUid);
    this.newDsr.toInstitutionUid = producerUid;
    this.selectedServiceUid.set('');
    this.newDsr.serviceUid = '';
    this.selectedServiceDetail.set(null);
    this.uploadedAttachments.set([]);

    if (!producerUid) {
      this.producerServices.set([]);
      this.producerAttachmentTypes.set([]);
      return;
    }

    const cached = this.allActiveServices().filter(s =>
      s.institutionUid && String(s.institutionUid).toLowerCase() === String(producerUid).toLowerCase() && s.active
    );
    this.producerServices.set(cached);
    if (cached.length > 0) {
      this.onServiceChange(cached[0].uid);
    }

    this.loadingServices.set(true);
    this.catalogService.getServicesByInstitution(producerUid).subscribe({
      next: (res) => {
        this.loadingServices.set(false);
        if (res.success && res.data) {
          const activeOnly = res.data.filter(s => s.active);
          this.producerServices.set(activeOnly);
          if (activeOnly.length > 0 && (!this.selectedServiceUid() || !activeOnly.some(s => s.uid === this.selectedServiceUid()))) {
            this.onServiceChange(activeOnly[0].uid);
          }
        }
      },
      error: () => {
        this.loadingServices.set(false);
      }
    });

    // Also fetch producer compliance attachment types
    this.attachmentTypeService.getAllAttachmentTypes().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const matched = res.data.filter(t =>
            !t.institutionUid || String(t.institutionUid).toLowerCase() === String(producerUid).toLowerCase()
          );
          this.producerAttachmentTypes.set(matched.length > 0 ? matched : res.data);
        }
      }
    });
  }

  onServiceChange(serviceUid: string) {
    this.selectedServiceUid.set(serviceUid);
    this.newDsr.serviceUid = serviceUid;
    if (!serviceUid) {
      this.selectedServiceDetail.set(null);
      return;
    }

    // Fetch full service details to guarantee requiredAttachments are fresh
    this.catalogService.getServiceByUid(serviceUid).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.selectedServiceDetail.set(res.data);
        }
      }
    });
  }

  getSelectedProducerName(): string {
    const pUid = this.selectedProducerUid();
    const inst = this.onboardedInstitutions().find(i => i.uid === pUid);
    return inst ? inst.name : '';
  }

  getSelectedServiceName(): string {
    const sUid = this.selectedServiceUid();
    const svc = this.producerServices().find(s => s.uid === sUid)
      || this.allActiveServices().find(s => s.uid === sUid);
    return svc ? svc.name : '';
  }

  getAcceptAttribute(allowed?: string): string {
    if (!allowed || allowed.trim() === '' || allowed === 'Any') return '*';
    return allowed.split(',').map(ext => '.' + ext.trim().replace('.', '')).join(',');
  }

  previewUploadedFile(att: CreateDsrAttachmentRequest | DsrAttachmentResponse) {
    this.selectedUploadedPreview.set(att);
    this.showUploadPreviewModal.set(true);
  }

  isUploadedPdf(att: CreateDsrAttachmentRequest | DsrAttachmentResponse): boolean {
    const ext = (att.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (att.contentType || '').toLowerCase();
    return ext === 'pdf' || ct.includes('pdf');
  }

  isUploadedImage(att: CreateDsrAttachmentRequest | DsrAttachmentResponse): boolean {
    const ext = (att.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (att.contentType || '').toLowerCase();
    return ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext) || ct.includes('image');
  }

  formatFileSize(bytes?: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getUploadedForRequirement(req: ServiceRequiredAttachmentDto): CreateDsrAttachmentRequest | undefined {
    return this.uploadedAttachments().find(u =>
      (u.attachmentCode && req.code && u.attachmentCode.toLowerCase() === req.code.toLowerCase()) ||
      (u.attachmentName && req.name && u.attachmentName.toLowerCase() === req.name.toLowerCase())
    );
  }

  onFileSelected(event: Event, req?: ServiceRequiredAttachmentDto) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    // Validate size
    const maxMb = req?.maxSizeMb || 10;
    if (file.size > maxMb * 1024 * 1024) {
      this.toastService.error(`File size exceeds maximum allowed limit of ${maxMb}MB for "${req?.name || file.name}"`);
      input.value = '';
      return;
    }

    // Validate extension
    if (req?.allowedExtensions) {
      const allowed = req.allowedExtensions.toLowerCase().split(',').map(e => e.trim().replace('.', ''));
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!allowed.includes(ext)) {
        this.toastService.error(`Invalid format .${ext}. Document "${req.name}" must be: ${req.allowedExtensions}`);
        input.value = '';
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result as string;
      const attReq: CreateDsrAttachmentRequest = {
        attachmentName: req?.name || file.name,
        attachmentCode: req?.code || ('DOC_' + file.name.toUpperCase().replace(/[^A-Z0-9]/g, '_')),
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/octet-stream',
        fileData: base64Data
      };

      // Replace existing upload for same requirement or file name
      let current = this.uploadedAttachments().filter(u => {
        if (req) {
          const reqCodeMatch = u.attachmentCode && req.code && u.attachmentCode.toLowerCase() === req.code.toLowerCase();
          const reqNameMatch = u.attachmentName && req.name && u.attachmentName.toLowerCase() === req.name.toLowerCase();
          return !(reqCodeMatch || reqNameMatch);
        }
        return u.fileName !== file.name;
      });

      this.uploadedAttachments.set([...current, attReq]);
      this.toastService.success(`Uploaded: ${req?.name || file.name} (${file.name})`);
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  removeUploadedAttachment(att: CreateDsrAttachmentRequest) {
    this.uploadedAttachments.set(
      this.uploadedAttachments().filter(a => a !== att && a.fileName !== att.fileName)
    );
  }

  openCreateModal(producerUid?: string, serviceUid?: string) {
    this.modalError.set(null);
    const defaultConsumerService = this.consumerServices().length > 0
      ? this.consumerServices()[0].name
      : (this.authService.institutionName() + ' Core Gateway');

    this.newDsr = {
      toInstitutionUid: '',
      serviceUid: '',
      systemName: defaultConsumerService,
      environment: DsrEnvironment.TESTING,
      purpose: ''
    };
    this.selectedProducerUid.set('');
    this.selectedServiceUid.set('');
    this.selectedServiceDetail.set(null);
    this.producerAttachmentTypes.set([]);
    this.uploadedAttachments.set([]);
    this.producerServices.set([]);

    this.loadInstitutionsAndServices();
    this.showCreateModal.set(true);

    if (producerUid) {
      setTimeout(() => {
        this.onProducerChange(producerUid);
        if (serviceUid) {
          setTimeout(() => {
            this.onServiceChange(serviceUid);
          }, 300);
        }
      }, 150);
    }
  }

  onCreateDsr() {
    if (!this.isAttachmentValid()) {
      this.toastService.error('Please upload all mandatory compliance documents: ' + this.missingMandatoryNames());
      return;
    }

    this.modalError.set(null);
    const payload: CreateDsrRequest = {
      ...this.newDsr,
      environment: DsrEnvironment.TESTING,
      attachments: this.uploadedAttachments()
    };

    this.dsrService.createDsr(payload).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message);
          this.showCreateModal.set(false);
          this.uploadedAttachments.set([]);
          this.loadRequests();
          this.refreshCounts();
        } else if (res.success === false) {
          this.modalError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.modalError.set(extractErrorDetails(err.error || err));
      }
    });
  }

  openDetails(dsr: DataSharingRequestResponse) {
    this.selectedDsr.set(dsr);
    this.workflowComment = '';
    this.showDetailsModal.set(true);

    this.dsrService.getDsrByUid(dsr.uid).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.selectedDsr.set(res.data);
        }
      }
    });
  }

  async quickApprove(req: DataSharingRequestResponse) {
    const confirmed = await this.toastService.confirm({
      title: 'Approve Data Sharing Request',
      message: `Are you sure you want to approve request for "${req.serviceName}" from ${req.fromInstitutionName}?`,
      confirmText: 'Yes, Approve',
      cancelText: 'Cancel',
      confirmStyle: 'success'
    });
    if (!confirmed) return;
    this.onProcessAction(req.uid, 'APPROVE', 'Approved by institutional officer', true);
  }

  async quickReject(req: DataSharingRequestResponse) {
    const confirmed = await this.toastService.confirm({
      title: 'Reject Data Sharing Request',
      message: `Are you sure you want to reject request for "${req.serviceName}" from ${req.fromInstitutionName}?`,
      confirmText: 'Yes, Reject',
      cancelText: 'Cancel',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;
    this.onProcessAction(req.uid, 'REJECT', 'Rejected by institutional officer', true);
  }

  async onProcessAction(uid: string, action: 'APPROVE' | 'REJECT', customComment?: string, skipConfirm = false) {
    if (!skipConfirm) {
      const verb = action === 'APPROVE' ? 'approve' : 'reject';
      const confirmed = await this.toastService.confirm({
        title: action === 'APPROVE' ? 'Approve Data Sharing Request' : 'Reject Data Sharing Request',
        message: `Are you sure you want to ${verb} this Data Sharing Request?`,
        confirmText: action === 'APPROVE' ? 'Yes, Approve' : 'Yes, Reject',
        cancelText: 'Cancel',
        confirmStyle: action === 'APPROVE' ? 'success' : 'danger'
      });
      if (!confirmed) return;
    }

    const finalComment = customComment || this.workflowComment || (action === 'APPROVE' ? 'Approved by officer' : 'Rejected by officer');
    this.isProcessingAction.set(true);

    this.dsrService.processDsrAction(uid, { action, comment: finalComment }).subscribe({
      next: (res) => {
        this.isProcessingAction.set(false);
        this.toastService.success(res.message);
        if (res.data) this.selectedDsr.set(res.data);
        this.workflowComment = '';
        this.loadRequests();
        this.refreshCounts();
      },
      error: (err) => {
        this.isProcessingAction.set(false);
        this.toastService.error(err.error?.message || 'Failed to process DSR decision action');
      }
    });
  }

  async onTestConnection(uid: string) {
    const confirmed = await this.toastService.confirm({
      title: 'Test Security Server Connection',
      message: 'Are you sure you want to execute a live connection test to the security server endpoint?',
      confirmText: 'Yes, Test Connection',
      cancelText: 'Cancel',
      confirmStyle: 'primary'
    });
    if (!confirmed) return;

    this.isProcessingAction.set(true);
    this.dsrService.testConnection(uid).subscribe({
      next: (res) => {
        this.isProcessingAction.set(false);
        this.toastService.success(res.message);
        if (res.data) this.selectedDsr.set(res.data);
        this.loadRequests();
        this.refreshCounts();
      },
      error: (err) => {
        this.isProcessingAction.set(false);
        this.toastService.error(err.error?.message || 'Connection test failed');
      }
    });
  }

  async onRequestLiveConnection(uid: string) {
    const confirmed = await this.toastService.confirm({
      title: 'Authorize Production Live Stream',
      message: 'Are you sure you want to provision and activate live Central Server data stream access for this request?',
      confirmText: 'Yes, Authorize Live Stream',
      cancelText: 'Cancel',
      confirmStyle: 'success'
    });
    if (!confirmed) return;

    this.isProcessingAction.set(true);
    this.dsrService.liveConnectionRequest(uid).subscribe({
      next: (res) => {
        this.isProcessingAction.set(false);
        this.toastService.success(res.message);
        if (res.data) this.selectedDsr.set(res.data);
        this.loadRequests();
        this.refreshCounts();
      },
      error: (err) => {
        this.isProcessingAction.set(false);
        this.toastService.error(err.error?.message || 'Failed to authorize production live access');
      }
    });
  }

  canManageDocumentation(req: DataSharingRequestResponse | null | undefined): boolean {
    if (!req) return false;
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }
    return this.isIncomingRequest(req);
  }

  openUploadDocModal() {
    this.newDsrDoc.set({
      title: '',
      docType: 'OPEN_API',
      fileName: '',
      contentType: '',
      fileSize: 0,
      fileData: ''
    });
    this.showUploadDocModal.set(true);
  }

  onDsrDocFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    const current = this.newDsrDoc();
    let autoTitle = current.title.trim();
    if (!autoTitle) {
      autoTitle = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
    }

    let detectedType = current.docType;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (['json', 'yaml', 'yml'].includes(ext)) {
      detectedType = 'OPEN_API';
    } else if (ext === 'pdf') {
      detectedType = 'GUIDELINE';
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.newDsrDoc.set({
        title: autoTitle,
        docType: detectedType,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
        fileData: reader.result as string
      });
    };
    reader.readAsDataURL(file);
  }

  submitDsrDoc() {
    const dsr = this.selectedDsr();
    const docData = this.newDsrDoc();
    if (!dsr || !docData.fileData || !docData.title.trim()) {
      this.toastService.error('Please specify a title and select a specification file');
      return;
    }

    this.isUploadingDoc.set(true);
    const docPayload: ServiceDocumentationAttachmentDto = {
      title: docData.title.trim(),
      docType: docData.docType,
      fileName: docData.fileName,
      contentType: docData.contentType,
      fileSize: docData.fileSize,
      fileData: docData.fileData,
      uploadedAt: new Date().toISOString()
    };

    this.dsrService.uploadDocumentation(dsr.uid, docPayload).subscribe({
      next: (res) => {
        this.isUploadingDoc.set(false);
        this.showUploadDocModal.set(false);
        if (res.data) {
          this.selectedDsr.set(res.data);
          this.requests.update(list => list.map(item => item.uid === res.data.uid ? { ...item, serviceDocumentationAttachments: res.data.serviceDocumentationAttachments } : item));
        }
        this.toastService.success('Service documentation & API specification attached successfully');
      },
      error: (err) => {
        this.isUploadingDoc.set(false);
        this.toastService.error(err.error?.message || 'Failed to attach specification document');
      }
    });
  }

  async removeDsrDoc(doc: ServiceDocumentationAttachmentDto) {
    const dsr = this.selectedDsr();
    if (!dsr || !doc.uid) return;

    const confirmed = await this.toastService.confirm({
      title: 'Remove Specification',
      message: `Are you sure you want to remove "${doc.title}" (${doc.fileName}) from this data sharing request?`,
      confirmText: 'Yes, Remove',
      cancelText: 'Cancel',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;

    this.dsrService.removeDocumentation(dsr.uid, doc.uid).subscribe({
      next: (res) => {
        if (res.data) {
          this.selectedDsr.set(res.data);
          this.requests.update(list => list.map(item => item.uid === res.data.uid ? { ...item, serviceDocumentationAttachments: res.data.serviceDocumentationAttachments } : item));
        }
        this.toastService.success('Specification document removed successfully');
      },
      error: (err) => {
        this.toastService.error(err.error?.message || 'Failed to remove specification document');
      }
    });
  }

  openDocPreview(doc: ServiceDocumentationAttachmentDto) {
    this.previewDoc.set(doc);
    this.previewTextContent.set(null);

    if (this.isTextOrJson(doc) && doc.fileData) {
      try {
        const base64Data = doc.fileData.split(',')[1] || doc.fileData;
        const decoded = decodeURIComponent(escape(atob(base64Data)));
        try {
          const parsed = JSON.parse(decoded);
          this.previewTextContent.set(JSON.stringify(parsed, null, 2));
        } catch {
          this.previewTextContent.set(decoded);
        }
      } catch {
        this.previewTextContent.set(null);
      }
    }

    this.showDocPreviewModal.set(true);
  }

  isTextOrJson(doc: ServiceDocumentationAttachmentDto): boolean {
    const ext = (doc.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (doc.contentType || '').toLowerCase();
    return ['json', 'yaml', 'yml', 'txt', 'md', 'xml', 'csv'].includes(ext)
      || ct.includes('json') || ct.includes('yaml') || ct.includes('text') || ct.includes('xml');
  }

  isPdf(doc: ServiceDocumentationAttachmentDto): boolean {
    const ext = (doc.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (doc.contentType || '').toLowerCase();
    return ext === 'pdf' || ct.includes('pdf');
  }

  isImage(doc: ServiceDocumentationAttachmentDto): boolean {
    const ext = (doc.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (doc.contentType || '').toLowerCase();
    return ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext) || ct.includes('image');
  }

  copyPreviewContent() {
    const text = this.previewTextContent();
    if (text) {
      navigator.clipboard.writeText(text);
      this.toastService.success('Copied specification to clipboard');
    }
  }

  formatDocType(type?: string): string {
    switch (type) {
      case 'OPEN_API': return 'OpenAPI / Swagger';
      case 'GUIDELINE': return 'Guideline';
      case 'TECH_SPEC': return 'Technical Spec';
      case 'SCHEMA': return 'Payload Schema';
      default: return 'Documentation';
    }
  }
}

